import React, { useState, useRef, useCallback, useEffect } from 'react';
import { authApi } from '../firebase/config.js';
import { ENV } from '../config/env.js';
import ContentRenderer from './ContentRenderer.jsx';

const API_BASE_URL = ENV.apiBaseUrl;

function fuzzyScore(correct, user) {
    const norm = s => String(s || '').toLowerCase().trim().replace(/[^\w\s]/g, '');
    const c = norm(correct), u = norm(user);
    if (!u) return 0;
    if (c === u) return 100;
    if (c.includes(u) || u.includes(c)) return Math.max(55, 100 - Math.abs(c.length - u.length) * 3);
    const cWords = new Set(c.split(/\s+/).filter(Boolean));
    const hits = u.split(/\s+/).filter(w => w && cWords.has(w)).length;
    return Math.round((hits / Math.max(cWords.size, 1)) * 100);
}

const gradeLabel = score =>
    score >= 80 ? { label: '✓ Correct', cls: 'text-emerald-300', bg: 'border-emerald-500/30 bg-emerald-500/8', btn: 'bg-emerald-600 hover:bg-emerald-500' }
    : score >= 50 ? { label: '~ Almost', cls: 'text-amber-300', bg: 'border-amber-500/30 bg-amber-500/8', btn: 'bg-amber-600 hover:bg-amber-500' }
    : { label: '✗ Incorrect', cls: 'text-rose-300', bg: 'border-rose-500/30 bg-rose-500/8', btn: 'bg-rose-600 hover:bg-rose-500' };

export default function LearnMode({ folder, onClose }) {
    const [queue, setQueue] = useState(() => [...(folder.cards || [])].sort(() => Math.random() - 0.5));
    const [index, setIndex] = useState(0);
    const [userAnswer, setUserAnswer] = useState('');
    const [result, setResult] = useState(null);
    const [grading, setGrading] = useState(false);
    const [history, setHistory] = useState([]);
    const [done, setDone] = useState(false);
    const inputRef = useRef(null);

    const card = queue[index];
    const progressPct = queue.length > 0 ? (index / queue.length) * 100 : 0;
    const correct = history.filter(h => h.score >= 80).length;

    useEffect(() => {
        if (!result && inputRef.current) inputRef.current.focus();
    }, [index, result]);

    const handleSubmit = useCallback(async () => {
        if (!userAnswer.trim() || !card || grading) return;
        setGrading(true);
        let score = fuzzyScore(card.answer, userAnswer);
        try {
            const data = await authApi(`${API_BASE_URL}/score-answer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ correctAnswer: String(card.answer || ''), userAnswer }),
            });
            score = Number(data.score ?? score);
        } catch { /* use local score */ }
        setResult({ score, correctAnswer: card.answer });
        setHistory(prev => [...prev, { card, score, userAnswer }]);
        setGrading(false);
    }, [userAnswer, card, grading]);

    const handleNext = useCallback(() => {
        if (!result) return;
        // Add missed cards back to end of queue for review
        if (result.score < 60) setQueue(prev => [...prev, prev[index]]);
        setResult(null);
        setUserAnswer('');
        if (index + 1 >= queue.length) setDone(true);
        else setIndex(i => i + 1);
    }, [result, index, queue.length]);

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            result ? handleNext() : handleSubmit();
        }
    };

    const restart = () => {
        setQueue([...(folder.cards || [])].sort(() => Math.random() - 0.5));
        setIndex(0); setUserAnswer(''); setResult(null); setHistory([]); setDone(false);
    };

    if (done || (queue.length > 0 && index >= queue.length)) {
        const total = history.length;
        return (
            <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-slate-900/95 shadow-2xl p-8 text-center">
                    <div className="text-5xl mb-4">🎓</div>
                    <h2 className="text-2xl font-bold text-white mb-2">Session Complete!</h2>
                    <p className="text-slate-400 mb-1">{correct} of {total} answers correct (≥ 80%)</p>
                    <div className="h-2.5 rounded-full bg-white/10 my-5 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all"
                            style={{ width: `${total > 0 ? (correct / total) * 100 : 0}%` }} />
                    </div>
                    <div className="flex gap-3 justify-center flex-wrap">
                        <button onClick={restart}
                            className="rounded-full px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 transition">
                            Study Again
                        </button>
                        <button onClick={onClose}
                            className="rounded-full px-6 py-2.5 text-sm font-semibold text-slate-300 bg-white/5 ring-1 ring-white/10 hover:bg-white/10 transition">
                            Done
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!card) return null;
    const g = result ? gradeLabel(result.score) : null;

    return (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex flex-col px-2 py-3 sm:px-4 sm:py-5">
            <div className="mx-auto w-full max-w-2xl rounded-2xl border border-white/10 bg-slate-900/95 shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: '100%' }}>
                {/* Header */}
                <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b border-white/8">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="h-6 w-6 shrink-0 rounded-full bg-teal-500/20 flex items-center justify-center">
                            <svg className="h-3.5 w-3.5 text-teal-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </div>
                        <span className="text-sm font-semibold text-white truncate">Learn — {folder.name}</span>
                        <span className="shrink-0 text-xs text-teal-300 bg-teal-500/15 rounded-full px-2 py-0.5 tabular-nums">
                            {correct}/{history.length} correct
                        </span>
                    </div>
                    <button onClick={onClose} className="shrink-0 rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                {/* Progress */}
                <div className="h-1 bg-white/5 shrink-0">
                    <div className="h-full bg-gradient-to-r from-teal-400 to-emerald-400 transition-all duration-300" style={{ width: `${progressPct}%` }} />
                </div>
                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
                    <p className="text-xs text-slate-500 tabular-nums">{index + 1} of {queue.length}</p>

                    <div className="rounded-2xl border border-teal-500/20 bg-teal-500/5 p-5">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-teal-400/70 mb-2">Question</p>
                        <p className="text-white text-base leading-relaxed"><ContentRenderer content={card.question} /></p>
                    </div>

                    {!result ? (
                        <div>
                            <label className="block text-xs font-medium text-slate-400 mb-2">Your Answer</label>
                            <textarea
                                ref={inputRef}
                                value={userAnswer}
                                onChange={e => setUserAnswer(e.target.value)}
                                onKeyDown={onKeyDown}
                                rows={3}
                                placeholder="Type your answer… (Enter to submit)"
                                className="w-full rounded-xl bg-slate-800/60 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/40 resize-none"
                            />
                            <button onClick={handleSubmit} disabled={!userAnswer.trim() || grading}
                                className="mt-3 w-full rounded-xl py-3 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 disabled:opacity-40 transition">
                                {grading ? 'Checking…' : 'Submit (Enter)'}
                            </button>
                            <p className="text-center text-[10px] text-slate-600 mt-2">Shift+Enter for new line</p>
                        </div>
                    ) : (
                        <div className={`rounded-2xl border p-5 space-y-3 ${g.bg}`}>
                            <div className="flex items-center gap-3">
                                <span className={`text-2xl font-bold tabular-nums ${g.cls}`}>{result.score}%</span>
                                <span className={`text-sm font-semibold ${g.cls}`}>{g.label}</span>
                            </div>
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Your answer</p>
                                <p className="text-sm text-slate-300">{userAnswer}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Correct answer</p>
                                <p className="text-sm text-white"><ContentRenderer content={result.correctAnswer} /></p>
                            </div>
                            {result.score < 60 && <p className="text-xs text-rose-300/70">Card queued for review again.</p>}
                            <button onClick={handleNext} className={`w-full rounded-xl py-2.5 text-sm font-semibold text-white transition ${g.btn}`}>
                                {index + 1 >= queue.length ? 'Finish' : 'Next (Enter) →'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
